import React, { useEffect, useState } from 'react';
import { APIProvider, Map, AdvancedMarker, Pin, useMapsLibrary } from '@vis.gl/react-google-maps';

const API_KEY =
  process.env.GOOGLE_MAPS_PLATFORM_KEY ||
  (import.meta as any).env?.VITE_GOOGLE_MAPS_PLATFORM_KEY ||
  (globalThis as any).GOOGLE_MAPS_PLATFORM_KEY ||
  '';
const hasValidKey = Boolean(API_KEY) && API_KEY !== 'YOUR_API_KEY';

function MapComponent({ address }: { address: string }) {
  const placesLib = useMapsLibrary('places');
  const [position, setPosition] = useState<google.maps.LatLngLiteral | null>(null);

  useEffect(() => {
    if (!placesLib || !address) return;
    
    // Use Text Search to geocode the address
    placesLib.Place.searchByText({
      textQuery: address,
      fields: ['location'],
      maxResultCount: 1,
    }).then(({ places }) => {
      if (places && places[0] && places[0].location) {
        setPosition(places[0].location);
      }
    });
  }, [placesLib, address]);

  if (!position) return <div className="h-64 flex items-center justify-center bg-slate-100 rounded-xl text-slate-500">Loading map...</div>;

  return (
    <Map
      defaultCenter={position}
      defaultZoom={15}
      mapId="DEMO_MAP_ID"
      internalUsageAttributionIds={['gmp_mcp_codeassist_v1_aistudio']}
      style={{width: '100%', height: '100%'}}
    >
      <AdvancedMarker position={position}>
        <Pin background="#4285F4" glyphColor="#fff" />
      </AdvancedMarker>
    </Map>
  );
}

export const OpportunityMap = ({ address }: { address: string }) => {
  const [dynamicKey, setDynamicKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string>('');

  useEffect(() => {
    if (hasValidKey) {
      setDynamicKey(API_KEY);
      setLoading(false);
      setDebugInfo('Using compile-time API key');
      return;
    }

    setDebugInfo('Attempting runtime key fetch...');
    // Attempt to load the API key from the backend at runtime
    fetch('/api/maps-key')
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON (received HTML/fallback)');
        }
        return res.json();
      })
      .then(data => {
        if (data && data.key && data.key.trim() !== '' && data.key !== 'YOUR_API_KEY') {
          setDynamicKey(data.key);
          setDebugInfo('Runtime key loaded successfully');
        } else {
          const receivedVal = data && typeof data.key === 'string' ? `"${data.key.slice(0, 5)}..."` : 'undefined';
          setDebugInfo(`Key validation failed (key: ${receivedVal})`);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching dynamic maps key:', err);
        setDebugInfo(`Fetch failed: ${err.message || err}`);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="h-64 flex flex-col items-center justify-center bg-slate-100 rounded-xl text-slate-500 space-y-2">
        <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm font-medium">Loading map configuration...</span>
        <span className="text-[10px] text-slate-400 font-mono">{debugInfo}</span>
      </div>
    );
  }

  if (!dynamicKey) {
    return (
      <div className="h-64 flex flex-col items-center justify-center bg-slate-100 rounded-xl text-slate-500 p-4 text-center">
        <span className="text-sm font-medium mb-1">Map setup required.</span>
        <span className="text-[10px] text-slate-400 font-mono bg-slate-200/50 px-2 py-1 rounded">
          Status: {debugInfo}
        </span>
      </div>
    );
  }

  return (
    <APIProvider apiKey={dynamicKey} version="weekly">
      <div className="h-64 rounded-xl overflow-hidden">
        <MapComponent address={address} />
      </div>
    </APIProvider>
  );
};
